import {IconButton, HStack, Box, Spinner, Text} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import React, {useId, useEffect} from 'react'
import {useIntl} from 'react-intl'
import Field from '../field'
import {GpsLocationIcon, SearchIcon} from '../icons'
import useFindStoreFields from './useFindStoreFields'
import useGeolocationFindStoreFields from './useGeolocationFindStoreFields'
import {useWatch} from 'react-hook-form'
import {useLocation} from 'react-router-dom'
import useAutocompleteFindStoreFields from './useAutocompleteFindStoreFields'

const FindStoreFields = ({
    form,
    prefix = '',
    addressFieldId,
    inputProps,
    onLocationChange,
    onError,
    formLabel,
    onSubmit
}) => {
    const addressDescriptionId = useId()
    const fields = useFindStoreFields({form, prefix})
    const intl = useIntl()
    const address = useWatch({control: form.control, name: 'address'}) || ''
    const disabled = address.length < 1

    const {supported, loading, getCurrentPosition} = useGeolocationFindStoreFields({
        form,
        onGpsLocation: onLocationChange,
        onError
    })

    const {addressInputProps} = useAutocompleteFindStoreFields(form, {
        onPlaceSelected: () => onLocationChange()
    })

    const {search} = useLocation()

    // Reset form when URL parameters change
    useEffect(() => {
        const params = new URLSearchParams(search)
        const address = params.get('address') || ''
        form.setValue('address', address)
        if (!address) {
            form.setValue('latitude', '')
            form.setValue('longitude', '')
        }
    }, [search, form])

    return (
        <Box>
            <Field {...fields.latitude} />
            <Field {...fields.longitude} />
            <Text id={addressDescriptionId} tabIndex="-1" className="sr-only">
                {intl.formatMessage({
                    id: 'find_store_fields.search.assistive_msg.description',
                    defaultMessage:
                        'Begin typing to search, use arrow keys to navigate, Enter to select'
                })}
            </Text>
            <HStack spacing={2} width={'100%'} minW={0} position={'relative'} zIndex={10}>
                <Field
                    {...fields.address}
                    type="place-autocomplete"
                    {...(formLabel ? {label: formLabel} : {})}
                    inputProps={{
                        ...fields.address.inputProps,
                        ...addressInputProps,
                        hostProps: {
                            id: addressFieldId,
                            'aria-describedby': addressDescriptionId,
                            'aria-label':
                                inputProps?.['aria-label'] ??
                                (typeof formLabel === 'string' ? formLabel : undefined),
                            variant: 'flushed',
                            paddingRight: (disabled ? 0 : 9) + (supported ? 9 : 0)
                        },
                        minHeight: '3.12rem',
                        // callback for lat/lng
                        ...inputProps
                    }}
                    rightElement={
                        loading ? (
                            <Spinner size="sm" />
                        ) : (
                            supported && (
                                <IconButton
                                    aria-label={intl.formatMessage({
                                        id: 'find_store_fields.button.assistive_msg.gps_location',
                                        defaultMessage: 'Use current GPS location'
                                    })}
                                    boxSize={8}
                                    minWidth={8}
                                    lineHeight="1"
                                    icon={<GpsLocationIcon boxSize={4} />}
                                    variant="unstyled"
                                    onClick={getCurrentPosition}
                                />
                            )
                        )
                    }
                />
                <IconButton
                    form="findStoreForm"
                    onClick={async () => {
                        const currentData = {
                            latitude: '',
                            longitude: '',
                            address: form.getValues('address')
                        }

                        await onSubmit(currentData)
                    }}
                    aria-label={intl.formatMessage({
                        id: 'find_store_fields.button.assistive_msg.submit',

                        defaultMessage: 'Show nearby stores'
                    })}
                    boxSize={14}
                    minWidth={14}
                    flexShrink={0}
                    variant={disabled ? 'outline' : 'solid'}
                    icon={<SearchIcon boxSize={6} />}
                    isLoading={form.formState.isSubmitting}
                    disabled={disabled}
                    {...(formLabel ? {marginTop: 6} : {})}
                />
            </HStack>
        </Box>
    )
}

FindStoreFields.propTypes = {
    /** Object returned from `useForm` */
    form: PropTypes.object.isRequired,

    /** Optional input props to pass to the address field */
    inputProps: PropTypes.object,

    /** Optional prefix for field names */
    prefix: PropTypes.string,

    /** Function to call when a location is selected/ found per gps location */
    onLocationChange: PropTypes.func,

    /** Function to call when an error occurs */
    onError: PropTypes.func,

    addressFieldId: PropTypes.string,
    formLabel: PropTypes.object,
    onSubmit: PropTypes.func
}

export default FindStoreFields
